import { appSettings } from 'AppSettings';
import { PatientReviewScore, PatientReview, ReviewStatus } from './PatientReviewData';
import { BaseGet, BasePut } from './BaseData';
import { MeasurementNormality } from './MeasurementDefinitionData';
import assertExhaustive from '../researchProjectManagement/patientApproval/utils/assertExhaustive';

export interface PatientApprovalPatient {
  researchProjectId: string;
  deidentity: string;
  yearOfBirth?: number;
  state?: string;
  sampleBiobanks: string[];

  statisticTotalCount: number;

  labTotalCount: number;
  labCuratedTotalCount: number;
  labTotalWithIssuesCount: number;
  labCuratedTotalWithIssuesCount: number;
  labTotalWithoutIssuesCount: number;
  labCuratedTotalWithoutIssuesCount: number;

  obsTotalCount: number;
  obsCuratedTotalCount: number;
  obsTotalWithIssuesCount: number;
  obsCuratedTotalWithIssuesCount: number;
  obsTotalWithoutIssuesCount: number;
  obsCuratedTotalWithoutIssuesCount: number;

  medTotalCount: number;
  medCuratedTotalCount: number;
  medTotalWithIssuesCount: number;
  medTotalWithoutIssuesCount: number;
  medCuratedTotalWithIssuesCount: number;
  medCuratedTotalWithoutIssuesCount: number;

  procTotalCount: number;
  procTotalWithIssuesCount: number;
  procTotalWithoutIssuesCount: number;

  problemTotalCount: number;
  problemTotalWithIssuesCount: number;
  problemTotalWithoutIssuesCount: number;

  patientNotes: string[];
  sampleNotes: string[];

  systemReviewCategory: string | null;
  systemReviewStatusId: ReviewStatus['reviewStatusId'] | null;
  systemReviewScore: PatientReviewScore | null;
  systemReviewNotes: string | null;

  manualReviewStatusId: ReviewStatus['reviewStatusId'] | null;
  manualReviewScore: PatientReviewScore | null;
  manualReviewNotes: string | null;
}

export interface PatientApprovalPatientTest {
  test: string;
  type: string;
  codes: string[];
}

export interface PatientApprovalPatientWeeklyCount {
  performedWeek: string;
  type: string;
  totalCount: number;
  noIssuesCount: number;
}

export interface PatientApprovalPatientMeasurement {
  type: string;
  performedWeek: string;
  test: string;
  codes: string[];
  average: number;
  units: string;
  normality: MeasurementNormality;
  issues: string[];
  rowIds: string[];
  pipelineId: string;
}

export interface PatientMeasurementIssueUpdate {
  entity: string;
  issues: number[];
  rowIds: string[];
  pipelineId: string;
}

export const PATIENT_APPROVAL_EVENT_TYPES = ['diagnosis', 'medication'] as const;
export type PatientApprovalEventType = (typeof PATIENT_APPROVAL_EVENT_TYPES)[number];

export interface PatientApprovalEvent {
  name: string;
  eventDate: string;
  type: PatientApprovalEventType;
  issues: string[];
}

export interface PatientApprovalSampleCollectionDate {
  sampleCount: number;
  collectionDate: string;
}

export const PATIENT_JOURNEY_ENTITIES = [
  'problem',
  'observation',
  'medication',
  'procedure',
  'lab',
  'pathReport',
] as const;
export type PatientJourneyEntity = (typeof PATIENT_JOURNEY_ENTITIES)[number];

export const getPatientJourneyEntityDisplayName = (entity: PatientJourneyEntity) => {
  switch (entity) {
    case 'lab':
      return 'Labs';
    case 'medication':
      return 'Medications';
    case 'observation':
      return 'Observations';
    case 'problem':
      return 'Problems';
    case 'procedure':
      return 'Procedures';
    case 'pathReport':
      return 'Path Reports';
    default:
      assertExhaustive(entity);
  }
};

export interface PatientJourneyRow {
  id: string;
  date: string;
  entity: PatientJourneyEntity;
  vocabulary: string | null;
  vocabularyCode: string | null;
  name: string;
  quanValue: number | null;
  qualValue: string | null;
  units: string | null;
  issues: string[];
}

export interface PatientApprovalPatientData {
  tests: PatientApprovalPatientTest[];
  weeklyCounts: PatientApprovalPatientWeeklyCount[];
  measurements: PatientApprovalPatientMeasurement[];
  events: PatientApprovalEvent[];
  sampleCollectionDates: PatientApprovalSampleCollectionDate[];
  patientJourney: PatientJourneyRow[];
  manualReview: PatientReview | null;
  systemReview: PatientReview | null;
}

export async function GetResearchProjectApprovalPatients(
  researchProjectId: string,
  accessToken: string | undefined
): Promise<PatientApprovalPatient[] | undefined> {
  return await BaseGet(
    `${appSettings.api.endpoint}/api/v2/PatientApproval/ResearchProject/${researchProjectId}/Patients`,
    accessToken
  );
}

export async function GetPatientTests(
  researchProjectId: string,
  deidentity: string,
  accessToken: string | undefined
): Promise<PatientApprovalPatientTest[] | undefined> {
  return await BaseGet(
    `${
      appSettings.api.endpoint
    }/api/v2/PatientApproval/ResearchProject/${researchProjectId}/Patient/${encodeURIComponent(deidentity)}/Tests`,
    accessToken
  );
}

export async function GetPatientWeeklyCounts(
  researchProjectId: string,
  deidentity: string,
  accessToken: string | undefined
): Promise<PatientApprovalPatientWeeklyCount[] | undefined> {
  return await BaseGet(
    `${
      appSettings.api.endpoint
    }/api/v2/PatientApproval/ResearchProject/${researchProjectId}/Patient/${encodeURIComponent(
      deidentity
    )}/WeeklyCounts`,
    accessToken
  );
}

export async function GetPatientMeasurements(
  researchProjectId: string,
  deidentity: string,
  accessToken: string | undefined
): Promise<PatientApprovalPatientMeasurement[] | undefined> {
  return await BaseGet(
    `${
      appSettings.api.endpoint
    }/api/v2/PatientApproval/ResearchProject/${researchProjectId}/Patient/${encodeURIComponent(
      deidentity
    )}/Measurements`,
    accessToken
  );
}

export async function UpdatePatientMeasurementIssues(
  researchProjectId: string,
  deidentity: string,
  data: PatientMeasurementIssueUpdate,
  accessToken: string | undefined
): Promise<PatientApprovalPatientMeasurement[] | undefined> {
  return await BasePut(
    `${
      appSettings.api.endpoint
    }/api/v2/PatientApproval/ResearchProject/${researchProjectId}/Patient/${encodeURIComponent(
      deidentity
    )}/Measurements`,
    data,
    accessToken
  );
}

export async function GetPatientEvents(
  researchProjectId: string,
  deidentity: string,
  accessToken: string | undefined
): Promise<PatientApprovalEvent[] | undefined> {
  return await BaseGet(
    `${
      appSettings.api.endpoint
    }/api/v2/PatientApproval/ResearchProject/${researchProjectId}/Patient/${encodeURIComponent(deidentity)}/Events`,
    accessToken
  );
}

export async function GetSampleCollectionDates(
  deidentity: string,
  accessToken: string | undefined
): Promise<PatientApprovalSampleCollectionDate[] | undefined> {
  return await BaseGet(
    `${appSettings.api.endpoint}/api/v2/PatientApproval/Patient/${encodeURIComponent(
      deidentity
    )}/SampleCollectionDates`,
    accessToken
  );
}

export async function GetPatientJourney(
  researchProjectId: string,
  deidentity: string,
  accessToken: string | undefined
): Promise<PatientJourneyRow[] | undefined> {
  return await BaseGet(
    `${
      appSettings.api.endpoint
    }/api/v2/PatientApproval/ResearchProject/${researchProjectId}/Patient/${encodeURIComponent(deidentity)}/Journey`,
    accessToken
  );
}

export async function GetPatientLatestManualReview(
  researchProjectId: string,
  deidentity: string,
  accessToken: string | undefined
): Promise<PatientReview | undefined> {
  return await BaseGet(
    `${
      appSettings.api.endpoint
    }/api/v2/PatientApproval/ResearchProject/${researchProjectId}/Patient/${encodeURIComponent(
      deidentity
    )}/LatestManualReview`,
    accessToken
  );
}

export async function GetPatientLatestSystemReview(
  researchProjectId: string,
  deidentity: string,
  accessToken: string | undefined
): Promise<PatientReview | undefined> {
  return await BaseGet(
    `${
      appSettings.api.endpoint
    }/api/v2/PatientApproval/ResearchProject/${researchProjectId}/Patient/${encodeURIComponent(
      deidentity
    )}/LatestSystemReview`,
    accessToken
  );
}
